/**
 * Manager PA Layout - re-skinned to match the client-pwa v1 template
 * (light theme, RoniDoc CSS tokens, bottom-nav + header pattern). PA-specific
 * mic + voice modal + query bar + slide-out menu + alerts are preserved.
 */

import { useState, useEffect, useRef, useCallback } from 'react';
import { Outlet, NavLink, Link, useLocation, useNavigate } from 'react-router-dom';
import {
  Home, Mic, MicOff, Calendar, Mail, CheckSquare, Menu, X, LogOut, Sparkles,
  Download, History, Bell, Send, ChevronRight, ChevronDown, Settings as SettingsIcon,
  CalendarClock, Users, Activity, UserCheck, AlertTriangle, MessageCircle, LayoutGrid, Keyboard, Square,
} from 'lucide-react';
import { VoiceWaveform, haptic, SuccessCheck } from '@/components/pwa/animations';
import { useManager, logout as managerLogout } from '@/services/api/manager-auth';
import maClient from '@/services/api/ma-client';
import { cn } from '@/lib/utils';
import { InstallPrompt } from '@/components/pwa/InstallPrompt';
import { ManagerThemeProvider, useManagerTheme, paAccentVars, ThemeToggle } from './theme';
import { pa } from './base';
import { ActiveClientProvider, useActiveClient } from './active-client';
import { MessagesHeaderProvider, useMessagesHeader } from './messages-header';
import ClientPicker from './ClientPicker';

/**
 * Convert a recorded audio blob (webm/opus on Chrome, mp4/aac on Safari) to a
 * mono 16-bit WAV, which every browser - including Safari/iOS - can play. Each
 * platform can decode its OWN recording, and WAV is universally playable, so
 * voice notes recorded on one device play on any other.
 */
async function blobToWav(blob: Blob): Promise<Blob> {
  const AC: any = (window as any).AudioContext || (window as any).webkitAudioContext;
  const ctx = new AC();
  try {
    const audioBuffer: AudioBuffer = await ctx.decodeAudioData(await blob.arrayBuffer());
    const length = audioBuffer.length;
    const numCh = audioBuffer.numberOfChannels || 1;
    const rate = audioBuffer.sampleRate;
    const mono = new Float32Array(length);
    for (let ch = 0; ch < numCh; ch++) {
      const data = audioBuffer.getChannelData(ch);
      for (let i = 0; i < length; i++) mono[i] += data[i] / numCh;
    }
    const buffer = new ArrayBuffer(44 + length * 2);
    const view = new DataView(buffer);
    const writeStr = (off: number, s: string) => { for (let i = 0; i < s.length; i++) view.setUint8(off + i, s.charCodeAt(i)); };
    writeStr(0, 'RIFF'); view.setUint32(4, 36 + length * 2, true); writeStr(8, 'WAVE');
    writeStr(12, 'fmt '); view.setUint32(16, 16, true); view.setUint16(20, 1, true); view.setUint16(22, 1, true);
    view.setUint32(24, rate, true); view.setUint32(28, rate * 2, true); view.setUint16(32, 2, true); view.setUint16(34, 16, true);
    writeStr(36, 'data'); view.setUint32(40, length * 2, true);
    let off = 44;
    for (let i = 0; i < length; i++) { const s = Math.max(-1, Math.min(1, mono[i])); view.setInt16(off, s < 0 ? s * 0x8000 : s * 0x7fff, true); off += 2; }
    return new Blob([buffer], { type: 'audio/wav' });
  } finally {
    try { ctx.close?.(); } catch { /* ignore */ }
  }
}

const NAV_ITEMS = [
  { href: pa(), icon: LayoutGrid, label: 'Dashboard', end: true },
  { href: pa('/voice'), icon: MessageCircle, label: 'Chat' },
  { href: pa('/todo'), icon: CheckSquare, label: 'Tasks' },
  { href: pa('/messages'), icon: Mail, label: 'Messages' },
];

function ManagerShell() {
  const { resolvedTheme } = useManagerTheme();
  const location = useLocation();
  const navigate = useNavigate();
  const manager = useManager();
  const { activeClient } = useActiveClient();
  const [clientPickerOpen, setClientPickerOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [inputText, setInputText] = useState('');
  const [inputMode, setInputMode] = useState<'talk' | 'type'>('talk');
  const [typeClosing, setTypeClosing] = useState(false);
  // Slide the type box down before swapping back to the talk circle.
  const goTalk = useCallback(() => {
    setTypeClosing(true);
    setTimeout(() => { setInputMode('talk'); setTypeClosing(false); }, 200);
  }, []);
  const [isListening, setIsListening] = useState(false);
  const [transcript, setTranscript] = useState('');
  const recognitionRef = useRef<any>(null);
  const transcriptRef = useRef('');      // live transcript (no React state race)
  const submitOnEndRef = useRef(false);  // submit the transcript when recognition ends

  const [showMicModal, setShowMicModal] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);

  // Submit query - stores in sessionStorage, navigates to voice page
  const handleQuerySubmit = useCallback((q: string) => {
    const query = q.trim();
    if (!query) return;
    // On the Messages page the input bar sends a MESSAGE (to the active client
    // thread or the staff channel), not an AI query - MessagesPage handles it.
    if (location.pathname.startsWith(pa('/messages'))) {
      window.dispatchEvent(new CustomEvent('pa-message', { detail: query }));
      return;
    }
    if (location.pathname === pa('/voice')) {
      // Already on the chat: deliver directly, no sessionStorage (a leftover item
      // would get re-fired on a later navigation).
      window.dispatchEvent(new CustomEvent('pa-query', { detail: query }));
    } else {
      // Hand off across the navigation; VoicePage drains it on arrival.
      sessionStorage.setItem('pa-pending-query', query);
      navigate(pa('/voice'));
    }
  }, [navigate, location.pathname]);

  // Keep the latest submit handler so the (persistent) recognition handlers
  // never call a stale closure.
  const submitQueryRef = useRef(handleQuerySubmit);
  useEffect(() => { submitQueryRef.current = handleQuerySubmit; });

  // Voice recognition (speech-to-text for the AI chat). We REUSE one persistent
  // SpeechRecognition instance - creating a new one each session is the cause of
  // the "works once then stops" bug (Chrome often won't fire results on a fresh
  // instance). Handlers read refs so the single instance is always current.
  const ensureRecognition = useCallback(() => {
    if (recognitionRef.current) return recognitionRef.current;
    const SR = (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition;
    if (!SR) return null;
    let rec: any;
    try { rec = new SR(); } catch { return null; }
    rec.continuous = true;
    rec.interimResults = true;
    rec.lang = 'en-AU';
    rec.onstart = () => { setIsListening(true); setTranscript(''); transcriptRef.current = ''; };
    rec.onresult = (e: any) => {
      let final = '', interim = '';
      for (let i = 0; i < e.results.length; i++) {
        if (e.results[i].isFinal) final += e.results[i][0].transcript;
        else interim += e.results[i][0].transcript;
      }
      const text = `${final} ${interim}`.replace(/\s+/g, ' ').trim();
      transcriptRef.current = text;   // synchronous - no React state race
      setTranscript(text);
    };
    rec.onend = () => {
      setIsListening(false);
      if (submitOnEndRef.current) {
        submitOnEndRef.current = false;
        const text = transcriptRef.current.trim();
        setShowMicModal(false);
        setTranscript('');
        transcriptRef.current = '';
        if (text) {
          submitQueryRef.current(text);
          setShowSuccess(true);
          setTimeout(() => setShowSuccess(false), 1500);
        } else {
          window.dispatchEvent(new CustomEvent('pa-voice-error', { detail: 'I didn\'t catch that - try speaking again.' }));
        }
      }
    };
    rec.onerror = (ev: any) => {
      setIsListening(false);
      if (ev?.error && ev.error !== 'no-speech' && ev.error !== 'aborted') {
        window.dispatchEvent(new CustomEvent('pa-voice-error', { detail: `Voice input error (${ev.error}).` }));
      }
    };
    recognitionRef.current = rec;
    return rec;
  }, []);

  const startListening = useCallback(() => {
    const rec = ensureRecognition();
    if (!rec) { window.dispatchEvent(new CustomEvent('pa-voice-error', { detail: 'Voice input isn\'t supported in this browser.' })); return; }
    submitOnEndRef.current = false;
    transcriptRef.current = '';
    setTranscript('');
    try {
      rec.start();
    } catch {
      // Already running (didn't fully reset) - abort and restart cleanly.
      try { rec.abort(); } catch { /* ignore */ }
      setTimeout(() => { try { rec.start(); } catch { /* ignore */ } }, 200);
    }
  }, [ensureRecognition]);

  // Stop listening and submit whatever was captured (done in onend).
  const stopListeningAndSubmit = useCallback(() => {
    submitOnEndRef.current = true;
    if (recognitionRef.current) { try { recognitionRef.current.stop(); } catch { /* ignore */ } }
    else { setShowMicModal(false); setIsListening(false); }
  }, []);

  const openMicModal = useCallback(() => {
    setShowMicModal(true);
    setTranscript('');
    setTimeout(() => startListening(), 200);
  }, [startListening]);

  // Voice notes (Messages page): the talk button records audio and hands the
  // blob to MessagesPage (via 'pa-voice-note') to send as an attachment - no
  // speech-to-text. Elsewhere the talk button keeps its dictation behaviour.
  const onMessagesPage = location.pathname.startsWith(pa('/messages'));
  const onChatPage = location.pathname === pa('/voice');
  const msgHeader = useMessagesHeader();
  const [voiceRecording, setVoiceRecording] = useState(false);
  const [recSecs, setRecSecs] = useState(0);
  const recTimerRef = useRef<any>(null);
  const voiceRecorderRef = useRef<MediaRecorder | null>(null);
  const voiceChunksRef = useRef<BlobPart[]>([]);
  const voiceStreamRef = useRef<MediaStream | null>(null);
  const voiceCancelRef = useRef(false);

  const startVoiceNote = useCallback(async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      voiceStreamRef.current = stream;
      const MR: any = (window as any).MediaRecorder;
      const mime = ['audio/webm', 'audio/mp4', 'audio/ogg'].find((m) => MR?.isTypeSupported?.(m)) || '';
      const rec: MediaRecorder = mime ? new MediaRecorder(stream, { mimeType: mime }) : new MediaRecorder(stream);
      voiceChunksRef.current = [];
      voiceCancelRef.current = false;
      rec.ondataavailable = (e) => { if (e.data && e.data.size > 0) voiceChunksRef.current.push(e.data); };
      rec.onstop = async () => {
        const type = rec.mimeType || mime || 'audio/webm';
        const raw = new Blob(voiceChunksRef.current, { type });
        voiceStreamRef.current?.getTracks().forEach((t) => t.stop());
        voiceStreamRef.current = null;
        if (voiceCancelRef.current || raw.size === 0) return;
        // Re-encode to WAV so it plays on every browser (Safari/iOS can't play
        // webm/opus). Fall back to the raw recording if conversion fails.
        try {
          const wav = await blobToWav(raw);
          window.dispatchEvent(new CustomEvent('pa-voice-note', { detail: { blob: wav, mime: 'audio/wav' } }));
        } catch {
          window.dispatchEvent(new CustomEvent('pa-voice-note', { detail: { blob: raw, mime: type.split(';')[0] } }));
        }
      };
      rec.start();
      voiceRecorderRef.current = rec;
      setRecSecs(0);
      recTimerRef.current = setInterval(() => setRecSecs((s) => s + 1), 1000);
      setVoiceRecording(true);
      haptic('medium');
    } catch {
      setVoiceRecording(false);
      window.dispatchEvent(new CustomEvent('pa-voice-error', { detail: 'Microphone access is needed to record a voice note.' }));
    }
  }, []);

  const endRecording = useCallback((cancel: boolean) => {
    haptic('light');
    voiceCancelRef.current = cancel;
    if (recTimerRef.current) { clearInterval(recTimerRef.current); recTimerRef.current = null; }
    try { voiceRecorderRef.current?.stop(); } catch { /* ignore */ }
    voiceRecorderRef.current = null;
    setVoiceRecording(false);
  }, []);
  const stopVoiceNote = useCallback(() => endRecording(false), [endRecording]);
  const cancelVoiceNote = useCallback(() => endRecording(true), [endRecording]);

  const talkTap = useCallback(() => {
    if (location.pathname.startsWith(pa('/messages'))) {
      if (!voiceRecording) startVoiceNote(); // stop is via the recording modal
    } else {
      haptic('light');
      openMicModal();
    }
  }, [location.pathname, voiceRecording, startVoiceNote, openMicModal]);

  // Discard a recording if we leave the Messages page mid-record.
  useEffect(() => {
    if (!onMessagesPage && voiceRecording) cancelVoiceNote();
  }, [onMessagesPage, voiceRecording, cancelVoiceNote]);

  // Brand this app as "Manager Assistant" while mounted: its own PWA manifest,
  // green theme-color, and document title. Restore on unmount so the firm panel
  // keeps its identity.
  useEffect(() => {
    const head = document.head;
    let manifestLink = document.querySelector<HTMLLinkElement>('link[rel="manifest"]');
    const prevManifest = manifestLink?.href;
    if (!manifestLink) { manifestLink = document.createElement('link'); manifestLink.rel = 'manifest'; head.appendChild(manifestLink); }
    manifestLink.href = '/panel/ma-manifest.json';

    let themeMeta = document.querySelector<HTMLMetaElement>('meta[name="theme-color"]');
    const prevTheme = themeMeta?.content;
    if (!themeMeta) { themeMeta = document.createElement('meta'); themeMeta.name = 'theme-color'; head.appendChild(themeMeta); }
    themeMeta.content = '#35AD88';

    const prevTitle = document.title;
    document.title = 'Manager Assistant';

    return () => {
      if (manifestLink && prevManifest) manifestLink.href = prevManifest;
      if (themeMeta && prevTheme) themeMeta.content = prevTheme;
      document.title = prevTitle;
    };
  }, []);

  // Prevent pinch / double-tap zoom while the Manager Assistant is mounted -
  // zooming crops the fixed layout. Installed PWAs honour the viewport flags;
  // in mobile Safari we also block the iOS gesture + multi-touch events. The
  // viewport is restored on unmount so the firm panel keeps normal zoom.
  useEffect(() => {
    const vp = document.querySelector<HTMLMetaElement>('meta[name="viewport"]');
    const prevVp = vp?.content;
    if (vp) vp.content = 'width=device-width, initial-scale=1, maximum-scale=1, user-scalable=no, viewport-fit=cover';

    // Disable pull-to-refresh / overscroll bounce (it drags the fixed header down).
    const html = document.documentElement, body = document.body;
    const prevHtmlOver = html.style.overscrollBehavior, prevBodyOver = body.style.overscrollBehavior;
    html.style.overscrollBehavior = 'none';
    body.style.overscrollBehavior = 'none';

    const block = (e: Event) => e.preventDefault();
    const onTouchMove = (e: TouchEvent) => { if (e.touches.length > 1) e.preventDefault(); };
    document.addEventListener('gesturestart', block);
    document.addEventListener('gesturechange', block);
    document.addEventListener('gestureend', block);
    document.addEventListener('touchmove', onTouchMove, { passive: false });

    return () => {
      if (vp && prevVp != null) vp.content = prevVp;
      html.style.overscrollBehavior = prevHtmlOver;
      body.style.overscrollBehavior = prevBodyOver;
      document.removeEventListener('gesturestart', block);
      document.removeEventListener('gesturechange', block);
      document.removeEventListener('gestureend', block);
      document.removeEventListener('touchmove', onTouchMove);
    };
  }, []);

  // Register service worker and request notification permission
  useEffect(() => {
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.register('/sw.js').catch(() => {});
      // Auto-apply SW updates so a new deploy refreshes the PA instead of
      // serving the stale cached shell. The SW calls skipWaiting() on install,
      // so a new worker takes control and fires controllerchange -> reload once.
      let reloading = false;
      navigator.serviceWorker.addEventListener('controllerchange', () => {
        if (reloading) return;
        reloading = true;
        window.location.reload();
      });
    }
    const timer = setTimeout(() => {
      if ('Notification' in window && Notification.permission === 'default') {
        Notification.requestPermission();
      }
    }, 5000);
    return () => clearTimeout(timer);
  }, []);

  // Periodic alert polling for browser notifications
  useEffect(() => {
    if (!('Notification' in window) || Notification.permission !== 'granted') return;
    const checkAlerts = async () => {
      try {
        const r = await maClient.get('/manager/alerts');
        {
          const data = r.data;
          const critical = data?.data?.summary?.critical || 0;
          if (critical > 0) {
            const lastNotified = localStorage.getItem('pa-last-notified');
            const now = Date.now();
            if (!lastNotified || now - parseInt(lastNotified) > 60 * 60 * 1000) {
              new Notification('Manager Assistant', {
                body: `${critical} critical alert${critical > 1 ? 's' : ''} need your attention`,
                icon: '/favicon.png',
                tag: 'ronidoc-critical',
              });
              localStorage.setItem('pa-last-notified', String(now));
            }
          }
        }
      } catch {}
    };
    checkAlerts();
    const interval = setInterval(checkAlerts, 30 * 60 * 1000);
    return () => clearInterval(interval);
  }, []);

  const handleLogout = async () => {
    managerLogout();
    window.location.href = pa('/login');
  };

  const displayName = manager?.displayName || manager?.email?.split('@')[0] || 'Manager';
  const userName = displayName.split(' ')[0];
  const initials = (displayName.split(' ').map(p => p[0]).join('').slice(0, 2) || 'M').toUpperCase();

  return (
    <div
      className={cn('flex flex-col h-[100dvh] overflow-hidden bg-background text-foreground', resolvedTheme === 'dark' && 'dark')}
      style={paAccentVars(resolvedTheme)}
    >

      <div className="mx-auto flex min-h-0 w-full max-w-[560px] flex-1 flex-col overflow-hidden">
        {/* Header - pinned at the top (shrink-0 in a fixed-height column) */}
        <header className="shrink-0 flex items-center justify-between border-b border-border/50 bg-card/95 px-5 pb-3 pt-[calc(env(safe-area-inset-top)+1.25rem)] backdrop-blur-md">
          {onChatPage ? (
            /* Chat page: active-client picker. */
            <button onClick={() => setClientPickerOpen(true)} className="flex min-w-0 items-center gap-2.5 text-left" aria-label="Choose active client">
              <img src="/panel/client/icon.png" alt="RoniDoc" className="h-9 w-9 shrink-0 rounded-lg" />
              <div className="min-w-0 leading-tight">
                {activeClient ? (
                  <>
                    <p className="flex items-center gap-1 text-sm font-semibold text-foreground">
                      <span className="truncate max-w-[150px]">{activeClient.name}</span>
                      <ChevronDown className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                    </p>
                    <p className="text-xs text-[var(--v4-accent)]">Active client</p>
                  </>
                ) : (
                  <>
                    <p className="flex items-center gap-1 text-sm font-semibold text-foreground">
                      All clients <ChevronDown className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                    </p>
                    <p className="text-xs text-muted-foreground">Tap to focus a client</p>
                  </>
                )}
              </div>
            </button>
          ) : onMessagesPage ? (
            /* Messages page: who the manager is chatting with (client or staff). */
            <div className="flex min-w-0 items-center gap-2.5">
              <img src="/panel/client/icon.png" alt="RoniDoc" className="h-9 w-9 shrink-0 rounded-lg" />
              <div className="min-w-0 leading-tight">
                <p className="truncate text-sm font-semibold text-foreground">{msgHeader.title || 'Messages'}</p>
                {msgHeader.sub && <p className="truncate text-xs text-muted-foreground">{msgHeader.sub}</p>}
              </div>
            </div>
          ) : (
            /* Everywhere else: plain RoniDoc branding. */
            <div className="flex min-w-0 items-center gap-2.5">
              <img src="/panel/client/icon.png" alt="RoniDoc" className="h-9 w-9 shrink-0 rounded-lg" />
              <p className="text-base font-semibold text-foreground">RoniDoc</p>
            </div>
          )}
          <div className="flex items-center gap-2">
            <div className="flex h-10 w-10 items-center justify-center rounded-full bg-[var(--v4-accent)] text-sm font-medium text-white">
              {initials.slice(0, 2)}
            </div>
            <button
              onClick={() => setMenuOpen(true)}
              className="flex h-10 w-10 items-center justify-center rounded-full bg-muted text-muted-foreground hover:bg-secondary"
              aria-label="Open menu"
            >
              <Menu className="h-4 w-4" />
            </button>
          </div>
        </header>

        <main className="flex-1 min-h-0 overflow-y-auto overscroll-contain">
          <Outlet context={{ language: 'en', onSubmitQuery: handleQuerySubmit }} />
        </main>

        {/* Bottom: input bar + nav - in-flow, pinned at the bottom of the column */}
        <div className="shrink-0">
          {/* Chat input bar: talk circle (default) with a toggle to typing.
              Fixed min-height + centered so talk and type modes are the same height. */}
          <div className="flex min-h-[84px] flex-col justify-center border-t border-border bg-card/95 px-3 pb-1 pt-2 backdrop-blur-md">
            {inputMode === 'talk' ? (
              <div className="flex items-center justify-center gap-4 py-1">
                <span className="h-12 w-12 shrink-0" aria-hidden />
                <button
                  onClick={talkTap}
                  className={cn(
                    'flex h-16 w-16 shrink-0 items-center justify-center rounded-full text-white transition hover:scale-105 active:scale-95',
                    voiceRecording ? 'bg-red-500 shadow-[0_0_28px_rgba(239,68,68,0.5)] animate-pulse' : 'bg-[var(--v4-accent)] shadow-[0_0_28px_rgba(53,173,136,0.45)]'
                  )}
                  aria-label={voiceRecording ? 'Stop and send voice note' : (onMessagesPage ? 'Record a voice note' : 'Tap to talk')}
                >
                  {voiceRecording ? <Square className="h-6 w-6 fill-current" /> : <Mic className="h-7 w-7" />}
                </button>
                <button
                  onClick={() => setInputMode('type')}
                  className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-muted text-muted-foreground transition hover:text-foreground active:scale-95"
                  aria-label="Type instead"
                >
                  <Keyboard className="h-6 w-6" />
                </button>
              </div>
            ) : (
              <div className={cn(
                'flex w-full items-end gap-2',
                typeClosing ? 'transition-transform duration-200 ease-in translate-y-full' : 'animate-in slide-in-from-bottom-4 duration-200'
              )}>
                <div className="flex-1 rounded-2xl border border-border bg-muted/40 px-4 py-2">
                  <textarea
                    placeholder={isListening ? 'Listening…' : (location.pathname.startsWith(pa('/messages')) ? 'Type a message…' : 'Ask about your clients…')}
                    value={inputText}
                    onChange={(e) => setInputText(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && !e.shiftKey && inputText.trim()) {
                        e.preventDefault();
                        handleQuerySubmit(inputText.trim());
                        setInputText('');
                      }
                    }}
                    className="w-full resize-none bg-transparent text-sm text-foreground outline-none placeholder:text-muted-foreground"
                    rows={2}
                    disabled={isListening}
                  />
                </div>
                <button
                  onClick={() => { if (!inputText.trim()) return; haptic('light'); handleQuerySubmit(inputText.trim()); setInputText(''); }}
                  disabled={!inputText.trim()}
                  className="mb-px flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-[var(--v4-accent)] text-white transition hover:bg-[var(--v4-accent-hover)] active:scale-95 disabled:opacity-40"
                  aria-label="Send"
                >
                  <Send className="h-4 w-4" />
                </button>
                <button
                  onClick={goTalk}
                  className="mb-px flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-muted text-muted-foreground transition hover:text-foreground active:scale-95"
                  aria-label="Talk instead"
                >
                  <Mic className="h-6 w-6" />
                </button>
              </div>
            )}
          </div>

          {/* Bottom nav */}
          <nav className="border-t border-border bg-card/95 px-2 py-2 pb-[calc(env(safe-area-inset-bottom)+0.5rem)] backdrop-blur-md">
            <div className="flex items-center justify-around">
              {NAV_ITEMS.map(({ href, icon: Icon, label, end }) => (
                <NavLink
                  key={href}
                  to={href}
                  end={end}
                  className={({ isActive }) =>
                    cn(
                      'flex flex-1 flex-col items-center gap-1 rounded-xl px-3 py-2 transition-colors',
                      isActive ? 'text-foreground' : 'text-muted-foreground hover:text-foreground'
                    )
                  }
                >
                  {({ isActive }) => (
                    <>
                      <Icon className={cn('h-5 w-5', isActive && 'stroke-[2.5] text-[var(--v4-accent)]')} />
                      <span className={cn('text-[10px] font-medium uppercase tracking-wider', isActive && 'font-semibold text-foreground')}>
                        {label}
                      </span>
                    </>
                  )}
                </NavLink>
              ))}
            </div>
          </nav>
        </div>
      </div>

      <InstallPrompt />

      {clientPickerOpen && <ClientPicker onClose={() => setClientPickerOpen(false)} />}

      {/* Slide-out menu (light, v1 style) */}
      {menuOpen && (
        <div className="fixed inset-0 z-[60]">
          <div onClick={() => setMenuOpen(false)} className="absolute inset-0 bg-black/40 backdrop-blur-sm animate-in fade-in duration-200" />
          <aside className="absolute right-0 top-0 bottom-0 w-72 max-w-[85%] flex flex-col bg-card border-l border-border shadow-2xl animate-in slide-in-from-right duration-300">
            {/* Header */}
            <div className="flex items-center justify-between px-5 py-4 border-b border-border">
              <div className="flex items-center gap-2">
                <Sparkles className="h-4 w-4 text-[var(--v4-accent)]" />
                <span className="text-sm font-semibold text-foreground">Settings</span>
              </div>
              <button
                onClick={() => setMenuOpen(false)}
                className="flex h-8 w-8 items-center justify-center rounded-full bg-muted text-muted-foreground hover:bg-secondary"
              >
                <X className="h-4 w-4" />
              </button>
            </div>

            {/* User card */}
            <div className="px-5 py-4 border-b border-border">
              <div className="flex items-center gap-3">
                <div className="flex h-12 w-12 items-center justify-center rounded-full bg-[var(--v4-accent)] text-base font-medium text-white">
                  {initials.slice(0, 2)}
                </div>
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-semibold text-foreground">{userName}</p>
                  <p className="truncate text-xs text-muted-foreground">{manager?.email}</p>
                </div>
              </div>
            </div>

            {/* Appearance */}
            <div className="px-5 py-4 border-b border-border">
              <p className="mb-2 text-xs font-medium uppercase tracking-wider text-muted-foreground">Appearance</p>
              <ThemeToggle />
            </div>

            {/* Menu items */}
            <nav className="flex-1 overflow-y-auto py-2">
              <p className="px-5 pb-1 pt-2 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Manage</p>
              <MenuItem to={pa('/deadlines')} icon={CalendarClock} label="Deadlines & compliance" onClick={() => setMenuOpen(false)} />
              <MenuItem to={pa('/team')} icon={Users} label="Team workload" onClick={() => setMenuOpen(false)} />
              <MenuItem to={pa('/timeline')} icon={Activity} label="Timeline" onClick={() => setMenuOpen(false)} />
              <MenuItem to={pa('/staff')} icon={UserCheck} label="Staff activity" onClick={() => setMenuOpen(false)} />
              <MenuItem to={pa('/alerts')} icon={AlertTriangle} label="Alerts" onClick={() => setMenuOpen(false)} />
              <p className="px-5 pb-1 pt-3 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">More</p>
              <MenuItem to={pa('/voice')} state={{ openHistory: true }} icon={History} label="Chat history" onClick={() => setMenuOpen(false)} />
              <MenuItem to={pa('/digest')} icon={Bell} label="Weekly digest" onClick={() => setMenuOpen(false)} />
              <MenuItem to={pa('/email') + '?view=connect'} icon={Mail} label="Email accounts" onClick={() => setMenuOpen(false)} />
              <MenuItem to={pa('/pulse')} icon={SettingsIcon} label="Pulse" onClick={() => setMenuOpen(false)} />
              <button
                onClick={() => {
                  setMenuOpen(false);
                  const dismissed = localStorage.getItem('pwa-install-dismissed');
                  if (dismissed) localStorage.removeItem('pwa-install-dismissed');
                  window.location.reload();
                }}
                className="flex w-full items-center gap-3 px-5 py-3.5 text-left transition-colors hover:bg-muted/50"
              >
                <Download className="h-4 w-4 text-muted-foreground" />
                <span className="flex-1 text-sm text-foreground">Install app</span>
                <ChevronRight className="h-4 w-4 text-muted-foreground" />
              </button>
            </nav>

            {/* Logout */}
            <div className="border-t border-border p-4">
              <button
                onClick={handleLogout}
                className="flex w-full items-center gap-3 rounded-xl px-3 py-3 text-rose-600 transition-colors hover:bg-rose-500/10 dark:text-rose-400"
              >
                <LogOut className="h-4 w-4" />
                <span className="text-sm font-medium">Log out</span>
              </button>
            </div>
          </aside>
        </div>
      )}


      <SuccessCheck show={showSuccess} />

      {/* Voice-note recording modal (Messages page) - records audio, sends as an attachment on stop */}
      {voiceRecording && (
        <div className="fixed inset-0 z-[75] flex flex-col items-center justify-center bg-slate-950/98 backdrop-blur-xl animate-in fade-in duration-200">
          <div className="flex flex-1 flex-col items-center justify-center gap-7 px-8">
            <VoiceWaveform isActive />
            <div className="text-center">
              <p className="text-lg font-medium text-white">Recording voice message…</p>
              <p className="mt-1 font-mono text-3xl tabular-nums text-[var(--v4-accent)]">
                {String(Math.floor(recSecs / 60)).padStart(2, '0')}:{String(recSecs % 60).padStart(2, '0')}
              </p>
              <p className="mt-2 text-sm text-slate-400">Tap stop to send it as a voice message</p>
            </div>
          </div>
          <div className="flex items-center justify-center gap-8 pb-[calc(env(safe-area-inset-bottom)+2.5rem)]">
            <button
              onClick={cancelVoiceNote}
              className="flex flex-col items-center gap-1.5 text-slate-400 active:scale-95"
              aria-label="Cancel recording"
            >
              <span className="flex h-14 w-14 items-center justify-center rounded-full bg-white/10"><X className="h-6 w-6" /></span>
              <span className="text-xs">Cancel</span>
            </button>
            <button
              onClick={stopVoiceNote}
              className="flex flex-col items-center gap-1.5 text-white active:scale-95"
              aria-label="Stop and send voice message"
            >
              <span className="flex h-20 w-20 items-center justify-center rounded-full bg-[var(--v4-accent)] shadow-[0_0_32px_rgba(53,173,136,0.5)]"><Send className="h-8 w-8" /></span>
              <span className="text-sm font-medium">Stop &amp; send</span>
            </button>
          </div>
        </div>
      )}

      {/* Full-screen mic modal - kept dark for focus */}
      {showMicModal && (
        <div className="fixed inset-0 z-[70] flex flex-col items-center justify-center bg-slate-950/98 backdrop-blur-xl animate-in fade-in duration-200">
          <div className="flex flex-1 flex-col items-center justify-center gap-6 px-8">
            <VoiceWaveform isActive={isListening} />
            <p className={cn(
              'max-w-sm text-center text-lg leading-relaxed transition-all duration-300',
              transcript ? 'text-white' : 'text-slate-500'
            )}>
              {transcript || (isListening ? 'Listening…' : 'Hold the mic button and speak…')}
            </p>
          </div>

          <div className="flex flex-col items-center pb-24">
            <button
              onClick={() => { haptic('medium'); if (isListening) stopListeningAndSubmit(); else startListening(); }}
              className={cn(
                'flex h-24 w-24 select-none items-center justify-center rounded-full transition-all duration-200',
                isListening
                  ? 'bg-red-500 shadow-[0_0_60px_rgba(239,68,68,0.5)] scale-110'
                  : 'bg-[var(--v4-accent)] shadow-[0_0_40px_rgba(53,173,136,0.4)] hover:scale-105 active:scale-95'
              )}
            >
              {isListening ? <MicOff className="h-10 w-10 text-white" /> : <Mic className="h-10 w-10 text-white" />}
            </button>
            <p className="mt-4 text-center text-xs text-slate-500">
              {isListening ? 'Tap to stop & send' : 'Tap to speak'}
            </p>
          </div>

          <button
            onClick={() => {
              submitOnEndRef.current = false;
              try { recognitionRef.current?.abort(); } catch { /* ignore */ }
              setIsListening(false);
              setShowMicModal(false);
              setTranscript('');
              transcriptRef.current = '';
            }}
            className="absolute top-4 right-4 p-3 text-slate-500 hover:text-white"
          >
            <X className="h-6 w-6" />
          </button>
        </div>
      )}
    </div>
  );
}

export default function ManagerLayout() {
  return (
    <ManagerThemeProvider>
      <ActiveClientProvider>
        <MessagesHeaderProvider>
          <ManagerShell />
        </MessagesHeaderProvider>
      </ActiveClientProvider>
    </ManagerThemeProvider>
  );
}

function MenuItem({
  to, icon: Icon, label, onClick, state,
}: { to: string; icon: any; label: string; onClick?: () => void; state?: any }) {
  return (
    <Link
      to={to}
      state={state}
      onClick={onClick}
      className="flex items-center gap-3 px-5 py-3.5 transition-colors hover:bg-muted/50"
    >
      <Icon className="h-4 w-4 text-muted-foreground" />
      <span className="flex-1 text-sm text-foreground">{label}</span>
      <ChevronRight className="h-4 w-4 text-muted-foreground" />
    </Link>
  );
}
